// app/types.ts
import { z } from 'zod';

// Generic API Response
export interface ApiResponse<T> {
  data: T;
  ok: boolean;
  status: number;
}

// Error Response
export interface ErrorResponse {
  odometer?: string[];
  [key: string]: any;
}

// Location Region for Map
export interface LocationRegion {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
}

// Employment Interface
export interface Employment {
  id?: number;
  user_email: string;
  role: string;
  is_active: boolean;
  company_nipc: string;
}

// Vehicle Interface
export interface Vehicle {
  id: number;
  registration_number: string;
  fuel_type: string;
  make: string;
  model: string;
  year: number;
  pending_activation?: boolean;
}

// TimeLog Entry
export interface TimeLogEntry {
  id: number;
  user: number;
  company: number;
  vehicle: number | null;
  timestamp: string;
  location: string | null;
  odometer: number | null;
  event_type: 'session_in' | 'session_out' | 'drive_in' | 'drive_out';
  company_name: string;
  vehicle_registration_number: string | null;
  observations: string | null;
}

// Form Schemas
export const companySchema = z.object({
  company_name: z.string().min(1, { message: 'Company name is required' }),
  company_nipc: z
    .string()
    .min(1, { message: 'NIPC is required' })
    .length(9, { message: 'NIPC must be 9 digits' })
    .regex(/^5\d{8}$/, { message: 'NIPC must start with 5 and be 9 digits' }),
  company_email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address' }),
});
export type CompanyFormData = z.infer<typeof companySchema>;

export const vehicleSchema = z.object({
  registration_number: z
    .string()
    .min(1, { message: 'Registration number is required' })
    .regex(/^[A-Z0-9]{2}-[A-Z0-9]{2}-[A-Z0-9]{2}$/, { message: 'Format must be XX-XX-XX (e.g., 12-AB-34)' }),
  fuel_type: z.enum(['gasolina', 'gasoleo', 'bifuel', 'hibrido', 'eletrico'], { message: 'Fuel type is required' }),
  make: z.string().min(1, { message: 'Make is required' }),
  model: z.string().min(1, { message: 'Model is required' }),
  year: z
    .number()
    .min(1886, { message: 'Year must be between 1886 and current year' })
    .max(new Date().getFullYear(), { message: 'Year must be between 1886 and current year' }),
});
export type VehicleFormData = z.infer<typeof vehicleSchema>;

export const odometerSchema = z.object({
  odometer: z.number().min(0, { message: 'Odometer must be a positive number' }),
  observations: z.string().optional(),
});
export type OdometerFormData = z.infer<typeof odometerSchema>;
